var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import ReactDOM from 'react-dom';
import { size } from '@floating-ui/core';
import { arrow, autoUpdate, flip, FloatingFocusManager, offset, shift, useFloating, useInteractions, } from '@floating-ui/react-dom-interactions';
import clsx from 'clsx';
import { canUseDOM, emptyFunction, KeyAttributeValue, useClasses, useIsomorphicLayoutEffect, useLayoutProps, useProps, } from '@hawkins/helpers';
import { spacingValues } from '@hawkins/variables';
import { useClickOutside, useCombineRefs } from '../hooks';
import { useFloatingUiStyles } from './floatingUi.styles';
var displayName = 'FloatingUI';
// singleton to prevent referential equality failures
var ORDER = ['reference', 'content'];
/**
 * The `FloatingUI` component uses the [useFloating](https://floating-ui.com/docs/react-dom) hook. This
 * component is fully controlled. It will only appear with the `open` prop. You can provide the `onOutsideClick`
 * callback to close when the user clicks outside of the `FloatingUI`.
 */
export var FloatingUI = React.forwardRef(function (_a, ref) {
    var _b;
    var _c;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _d = useProps(displayName, props), anchor = _d.anchor, _e = _d.arrow, arrowProp = _e === void 0 ? false : _e, _f = _d.arrowProps, arrowProps = _f === void 0 ? {} : _f, classesProp = _d.classes, className = _d.className, _g = _d.component, Component = _g === void 0 ? 'div' : _g, _h = _d.containerElement, containerElement = _h === void 0 ? document.body : _h, _j = _d.disableFlip, disableFlip = _j === void 0 ? false : _j, _k = _d.disablePortal, disablePortal = _k === void 0 ? false : _k, _l = _d.floatingUIProps, floatingUIProps = _l === void 0 ? {} : _l, _m = _d.focusManagerProps, focusManagerProps = _m === void 0 ? {} : _m, getFloatingUIInteractions = _d.getFloatingUIInteractions, id = _d.id, layout = _d.layout, maxHeight = _d.maxHeight, maxWidth = _d.maxWidth, _o = _d.offset, offsetProp = _o === void 0 ? spacingValues.space0 : _o, onEscPressed = _d.onEscPressed, onOpenChange = _d.onOpenChange, _p = _d.onOutsideClick, onOutsideClick = _p === void 0 ? emptyFunction : _p, open = _d.open, _q = _d.placement, placementProp = _q === void 0 ? 'bottom-start' : _q, renderTrigger = _d.renderTrigger, _r = _d.scrollWith, scrollWith = _r === void 0 ? false : _r, styleProp = _d.style, _s = _d.styled, styled = _s === void 0 ? true : _s, useClickOutsideParams = _d.useClickOutsideParams, _t = _d.useFocusManager, useFocusManager = _t === void 0 ? false : _t, _u = _d.widthVariant, widthVariant = _u === void 0 ? 'auto' : _u, _v = _d.elevated, elevated = _v === void 0 ? false : _v, restProps = __rest(_d, ["anchor", "arrow", "arrowProps", "classes", "className", "component", "containerElement", "disableFlip", "disablePortal", "floatingUIProps", "focusManagerProps", "getFloatingUIInteractions", "id", "layout", "maxHeight", "maxWidth", "offset", "onEscPressed", "onOpenChange", "onOutsideClick", "open", "placement", "renderTrigger", "scrollWith", "style", "styled", "useClickOutsideParams", "useFocusManager", "widthVariant", "elevated"]);
    // These flags make sure this component's logic remains backwards-compatible
    // while also allowing our internal components to utilize Floating UI interaction helpers.
    var hasInteractions = !!getFloatingUIInteractions;
    var hasOnOpenChange = !!onOpenChange;
    var hasTriggerRenderer = !!renderTrigger;
    // Styling
    var baseClasses = useFloatingUiStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_b = {},
        _b[classes.placementBottom] = placementProp.includes('bottom'),
        _b[classes.placementLeft] = placementProp.includes('left'),
        _b[classes.placementRight] = placementProp.includes('right'),
        _b[classes.placementTop] = placementProp.includes('top'),
        _b[classes.rootStyled] = styled,
        _b[classes.elevated] = elevated,
        _b));
    // Hooks
    var _w = __read(React.useState(), 2), width = _w[0], setWidth = _w[1];
    var arrowRef = React.useRef(null);
    var outsideClickRef = useClickOutside(__assign({ onOutsideClick: onOutsideClick, ignoreElement: anchor, ignoreFloatingUIElements: true }, useClickOutsideParams));
    // Handle esc, push the control off to user
    React.useEffect(function () {
        var handleKeyDown = function (event) {
            if (event.key === KeyAttributeValue.ESCAPE && onEscPressed) {
                onEscPressed(event);
            }
        };
        if (canUseDOM() && onEscPressed && open) {
            document.addEventListener('keydown', handleKeyDown);
            return function () {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [onEscPressed, open]);
    // Floating UI setup
    var _x = floatingUIProps.middleware, middleware = _x === void 0 ? [] : _x, restFloatingUIProps = __rest(floatingUIProps, ["middleware"]);
    var _y = useFloating(__assign({ onOpenChange: hasOnOpenChange ? onOpenChange : undefined, 
        // Only pass in the `open` param if the user has set `onOpenChange`.
        open: hasOnOpenChange ? open : undefined, placement: placementProp, middleware: __spreadArray([
            offset(offsetProp),
            (disableFlip ? false : flip()),
            shift({ padding: offsetProp }),
            arrow({ element: arrowRef }),
            size({
                apply: function (opts) {
                    var newStyle = {};
                    if (typeof maxHeight === 'number') {
                        newStyle.maxHeight = "".concat(Math.min(opts.availableHeight, maxHeight !== null && maxHeight !== void 0 ? maxHeight : Number.MAX_SAFE_INTEGER), "px");
                    }
                    if (typeof maxWidth === 'number') {
                        newStyle.maxWidth = "".concat(Math.min(opts.availableWidth, maxWidth !== null && maxWidth !== void 0 ? maxWidth : Number.MAX_SAFE_INTEGER), "px");
                    }
                    /**
                     * This is a piece of custom middleware that, after all the flipping and shifting is done,
                     * checks to see whether somebody is trying to override with a maxHeight or a maxWidth.
                     * Then, if the floating thing's maxHeight or maxWidth exceeds the actual space available
                     * in the viewport, uses the smaller value.
                     */
                    Object.assign(opts.elements.floating.style, newStyle);
                },
            })
        ], __read(middleware), false).filter(Boolean), whileElementsMounted: !scrollWith ? autoUpdate : undefined }, restFloatingUIProps)), x = _y.x, y = _y.y, reference = _y.reference, floating = _y.floating, placement = _y.placement, _z = _y.middlewareData.arrow, _0 = _z === void 0 ? {} : _z, arrowX = _0.x, arrowY = _0.y, context = _y.context;
    var floatingUiRef = useCombineRefs([floating, ref, outsideClickRef]);
    var _1 = useInteractions(hasInteractions ? getFloatingUIInteractions({ context: context }) : undefined), getFloatingProps = _1.getFloatingProps, getReferenceProps = _1.getReferenceProps;
    useIsomorphicLayoutEffect(function () {
        if (!hasTriggerRenderer)
            reference(anchor);
        if (widthVariant === 'full') {
            setWidth(anchor === null || anchor === void 0 ? void 0 : anchor.getBoundingClientRect().width);
        }
    }, [anchor, hasTriggerRenderer, reference, widthVariant]);
    var staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[placement.split('-')[0]] || '';
    var style1 = React.useMemo(function () { return (__assign({ top: y !== null && y !== void 0 ? y : 0, left: x !== null && x !== void 0 ? x : 0, width: typeof widthVariant === 'number' ? widthVariant : width }, style)); }, [style, width, widthVariant, x, y]);
    var style2 = React.useMemo(function () {
        var _a;
        return (_a = {
                left: arrowX != null ? "".concat(arrowX, "px") : '',
                top: arrowY != null ? "".concat(arrowY, "px") : '',
                right: '',
                bottom: ''
            },
            _a[staticSide] = -spacingValues.space0,
            _a);
    }, [arrowX, arrowY, staticSide]);
    var childrenComponent = React.useMemo(function () {
        var floatingProps = (hasInteractions ? getFloatingProps(restProps) : restProps);
        if (floatingProps.id && id) {
            floatingProps.id = id;
        }
        return (_jsxs(Component, __assign({ className: rootClassName, id: id, role: "tooltip", ref: floatingUiRef, style: style1 }, floatingProps, { children: [children, arrowProp && _jsx("div", __assign({ style: style2, ref: arrowRef, className: classes.arrow }, arrowProps))] })));
    }, [
        Component,
        arrowProp,
        arrowProps,
        children,
        classes.arrow,
        floatingUiRef,
        getFloatingProps,
        hasInteractions,
        id,
        restProps,
        rootClassName,
        style1,
        style2,
    ]);
    // Wrap in FloatingUI's focus manager if needed
    var renderComponent = React.useMemo(function () {
        return useFocusManager ? (_jsx(FloatingFocusManager, __assign({ context: context, modal: true, returnFocus: true, order: ORDER }, focusManagerProps, { children: childrenComponent }))) : (childrenComponent);
    }, [childrenComponent, context, focusManagerProps, useFocusManager]);
    var triggerElem = (_c = renderTrigger === null || renderTrigger === void 0 ? void 0 : renderTrigger({ floatingId: id, getReferenceProps: getReferenceProps, reference: reference })) !== null && _c !== void 0 ? _c : null;
    if (props.disabled || !props.open)
        return triggerElem;
    if (disablePortal) {
        return (_jsxs(_Fragment, { children: [triggerElem, renderComponent] }));
    }
    return (_jsxs(_Fragment, { children: [triggerElem, ReactDOM.createPortal(renderComponent, containerElement)] }));
});
FloatingUI.displayName = displayName;
